/**
 * Currency Switcher Component
 *
 * Dropdown to switch between supported currencies (EUR/GBP).
 * Uses Headless UI for accessible dropdown behavior.
 */

import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';
import { useCurrencyStore, CURRENCIES, type Currency } from '@/stores/useCurrencyStore';
import { cn } from '@/lib/utils';

interface CurrencySwitcherProps {
    variant?: 'default' | 'compact' | 'compact-dark';
    className?: string;
}

export const CurrencySwitcher = ({ variant = 'default', className }: CurrencySwitcherProps) => {
    const { currency, setCurrency, getCurrencyInfo } = useCurrencyStore();
    const currentCurrency = getCurrencyInfo();

    const currencyOptions = Object.values(CURRENCIES);

    return (
        <Menu as="div" className={cn('relative inline-block text-left', className)}>
            <Menu.Button
                className={cn(
                    'inline-flex min-h-[44px] items-center justify-center rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                    variant === 'compact'
                        ? 'gap-1 px-2 py-1 text-muted-foreground hover:text-foreground hover:bg-muted'
                        : variant === 'compact-dark'
                        ? 'gap-1 px-2 py-1 text-white/75 hover:text-white hover:bg-white/10 focus:ring-white'
                        : 'gap-2 px-3 py-2 text-foreground bg-white border border-border hover:bg-muted'
                )}
            >
                {/* Show the ISO code only — '€' + 'EUR' on the same trigger ('€EUR')
                    was redundant. The dropdown panel below still shows symbol + name
                    for each option, so users can still identify them by glyph. */}
                <span className="font-semibold">{currentCurrency.code}</span>
                <ChevronDown className={cn('h-4 w-4', (variant === 'compact' || variant === 'compact-dark') && 'hidden sm:block')} />
            </Menu.Button>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="absolute right-0 z-50 mt-2 min-w-[12rem] origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                        {currencyOptions.map((currencyOption) => (
                            <Menu.Item key={currencyOption.code}>
                                {({ active }) => (
                                    <button
                                        onClick={() => setCurrency(currencyOption.code as Currency)}
                                        className={cn(
                                            'flex w-full items-center gap-3 px-4 py-2 text-sm',
                                            active ? 'bg-muted text-primary' : 'text-foreground',
                                            currency === currencyOption.code && 'bg-primary/5 font-medium'
                                        )}
                                    >
                                        <span className="w-6 text-center font-semibold">
                                            {currencyOption.symbol}
                                        </span>
                                        <span>{currencyOption.name}</span>
                                        {currency === currencyOption.code && (
                                            <span className="ml-auto text-primary">✓</span>
                                        )}
                                    </button>
                                )}
                            </Menu.Item>
                        ))}
                    </div>
                </Menu.Items>
            </Transition>
        </Menu>
    );
};

export default CurrencySwitcher;
